import { useMutation } from '@tanstack/react-query';
import { Col, Flex, message, Row } from 'antd';
import HOPlanApi from 'apis/HOPlanApis';
import { PlantApi } from 'apis/PlantApis';
import ButtonExport from 'components/Button/ButtonExport';
import { Collapse } from 'components/Collapse/Collapse';
import { useTitle } from 'contexts/TitleProvider';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { exportExcelWithManySheet } from 'utils/constants/exportExcel';
import { generateUUID, IExportExcels } from 'utils/constants/functions';
import TableHOFollowPlanDetail from './TableHOFollowPlanDetail';

interface TableRef {
  getData: () => IExportExcels;
}

const processData = (data: any, key: string) => {
  const { revenue_plant: revenuePlant, ...restInvoice } = data[key];

  const res = [...revenuePlant, restInvoice];

  return res.reduce((acc, item) => {
    const {
      plant_name,
      total_year_revenue,
      total_revenue_year_actual,
      total_year_revenue_actual,
      total_year_percent,
      ...rest
    } = item;
    const arr: any[] = [
      {
        plant: plant_name ?? '-',
        title: 'Kế hoạch',
        id: generateUUID(),
        total: total_year_revenue,
      },
      {
        plant: plant_name ?? '-',
        title: 'Doanh thu thực tế',
        id: generateUUID(),
        total: total_revenue_year_actual ?? total_year_revenue_actual,
      },
      {
        plant: plant_name ?? '-',
        title: '%',
        id: generateUUID(),
        total: total_year_percent,
      },
    ];

    const keys = Object.keys(rest);

    keys.forEach((key) => {
      if (key.startsWith('month_revenue_actual_'))
        arr[1][key.replace('month_revenue_actual_', '')] = item[key];
      if (key.startsWith('total_month_revenue_actual_'))
        arr[1][key.replace('total_month_revenue_actual_', '')] = item[key];
      else if (key.startsWith('month_revenue_'))
        arr[0][key.replace('month_revenue_', '')] = item[key];
      else if (key.startsWith('total_month_revenue_'))
        arr[0][key.replace('total_month_revenue_', '')] = item[key];
      else if (key.startsWith('total_month_percent_'))
        arr[2][key.replace('total_month_percent_', '')] = item[key];
      else if (key.startsWith('total_'))
        arr[2][key.replace('total_', '')] = item[key];
      else if (key.startsWith('month_percent_'))
        arr[2][key.replace('month_percent_', '')] = item[key];
    });

    return [...acc, ...arr];
  }, []);
};

const refactorData = (data: any, plantId: string) => {
  if (!data) return [];

  if (plantId === '5000' || !plantId) return data;

  const length = data.length;

  return [...data].slice(0, length - 3);
};

const getItems = ({
  key,
  children,
  label,
  extra,
}: {
  key: string;
  label: string;
  children: ReactElement;
  extra?: ReactElement;
}) => {
  return {
    key,
    label,
    children,
    extra,
  };
};

const HOFollowPlanDetail = () => {
  const location = useLocation(),
    { setTitle, setBreadcrumb } = useTitle();
  const year = location?.state?.year ?? new Date().getFullYear();
  const plantId = location?.state?.plant_id || undefined;

  const invoiceRevenueTableRef = useRef<TableRef | null>(null);
  const processRevenueTableRef = useRef<TableRef | null>(null);
  const profitRevenueTableRef = useRef<TableRef | null>(null);
  const processTaxTableRef = useRef<TableRef | null>(null);
  const [plants, setPlants] = useState<any[]>([]);
  const [invoiceRevenue, setInvoiceRevenue] = useState<any>();
  const [processRevenue, setProcessRevenue] = useState<any>();
  const [profitRevenue, setProfitRevenue] = useState<any>();
  const [processTax, setProcessTax] = useState<any>();
  const [activeKeys, setActiveKeys] = useState<string[]>(['1', '2', '3', '4']);

  const invoiceRevenueProcess = useMemo(
    () => refactorData(invoiceRevenue, plantId),
    [invoiceRevenue, plantId],
  );
  const processRevenueProcess = useMemo(
    () => refactorData(processRevenue, plantId),
    [processRevenue, plantId],
  );
  const profitRevenueProcess = useMemo(
    () => refactorData(profitRevenue, plantId),
    [profitRevenue, plantId],
  );
  const processTaxProcess = useMemo(
    () => refactorData(processTax, plantId),
    [processTax, plantId],
  );

  const { mutate: getAllPlant } = useMutation({
    mutationFn: (id?: string) => PlantApi.getAllPlant(id),
    onSuccess: (res) => {
      setPlants(res.data.getPlant.items);
    },
    onError: (error) => console.log(error),
  });

  const {
    isPending: getTrackRevenueHoDetailLoading,
    mutate: getTrackRevenueHoDetail,
  } = useMutation({
    mutationFn: () =>
      HOPlanApi.getTrackRevenueHoDetail({
        year,
        plantId,
      }),
    onSuccess: (res) => {
      const data = res.data.getTrackRevenueDetailHo;

      setInvoiceRevenue(processData(data, 'invoice_revenue'));
      setProcessRevenue(processData(data, 'process_revenue'));
      setProfitRevenue(processData(data, 'profit_revenue'));
      setProcessTax(processData(data, 'profit_tax'));
    },
    onError: (error) => {
      console.log(error);
    },
  });

  useEffect(() => {
    const title =
      plantId === '5000' || !plantId
        ? `${year} - Theo dõi kế hoạch chi nhánh`
        : `${year} - [${plantId}] ${plants.find((item: any) => item.id === plantId)?.name ?? ''}`;

    setTitle(title);
    setBreadcrumb([
      { title: 'Vận hành' },
      { title: '[HO] theo dõi KH', href: '/ho-follow-plan' },
      { title: title },
    ]);
  }, [plantId, plants, setBreadcrumb, setTitle, year]);

  const handleClickDownload = async () => {
    // Mở tất cả các panel
    setActiveKeys(['1', '2', '3', '4']);

    // Đợi một chút để các component được render
    await new Promise((resolve) => setTimeout(resolve, 100));

    if (
      !invoiceRevenueTableRef.current ||
      !processRevenueTableRef.current ||
      !profitRevenueTableRef.current ||
      !processTaxTableRef.current
    ) {
      message.warning('Đang tải dữ liệu, vui lòng thử lại');
      return;
    }

    const invoiceRevenue = invoiceRevenueTableRef.current.getData();
    const processRevenue = processRevenueTableRef.current.getData();
    const profitRevenue = profitRevenueTableRef.current.getData();
    const processTax = processTaxTableRef.current.getData();

    exportExcelWithManySheet(
      [
        {
          data: processRevenue.datas,
          headerRow: processRevenue.headerRow,
          sheetName: 'Doanh thu công',
          isShowSTT: false,
        },
        {
          data: profitRevenue.datas,
          headerRow: profitRevenue.headerRow,
          sheetName: 'Lãi gộp',
          isShowSTT: false,
        },
        {
          data: processTax.datas,
          headerRow: processTax.headerRow,
          sheetName: 'Lợi nhuận trước thuế',
          isShowSTT: false,
        },
        {
          data: invoiceRevenue.datas,
          headerRow: invoiceRevenue.headerRow,
          sheetName: 'Doanh thu hóa đơn',
          isShowSTT: false,
        },
      ],
      '[HO] Theo dõi KH',
    );

    // Sau khi export xong, đóng lại các panel (tùy chọn)
    setActiveKeys([]);
  };

  useEffect(() => {
    getTrackRevenueHoDetail();
  }, [getTrackRevenueHoDetail]);

  useEffect(() => {
    getAllPlant('');
  }, [getAllPlant]);

  return (
    <Flex vertical gap={12}>
      <Row gutter={[8, 16]} align="middle" justify="end" className="mb-[17px]">
        <Col>
          <ButtonExport onClick={handleClickDownload} />
        </Col>
      </Row>
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        items={[
          getItems({
            key: '1',
            label: 'Doanh thu công',
            children: (
              <TableHOFollowPlanDetail
                year={year}
                data={processRevenueProcess}
                loading={getTrackRevenueHoDetailLoading}
                title={`${year} - Kế hoạch hội sở`}
                ref={processRevenueTableRef}
              />
            ),
            extra: <p>Đơn vị: triệu VND</p>,
          }),
        ]}
      />
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        items={[
          getItems({
            key: '2',
            label: 'Lãi gộp',
            children: (
              <TableHOFollowPlanDetail
                year={year}
                loading={getTrackRevenueHoDetailLoading}
                title={'Tiến độ'}
                data={profitRevenueProcess}
                ref={profitRevenueTableRef}
              />
            ),
            extra: <p>Đơn vị: triệu VND</p>,
          }),
        ]}
      />
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        items={[
          getItems({
            key: '3',
            label: 'Lợi nhuận trước thuế',
            children: (
              <TableHOFollowPlanDetail
                year={year}
                loading={getTrackRevenueHoDetailLoading}
                title={'Tiến độ'}
                data={processTaxProcess}
                ref={processTaxTableRef}
              />
            ),
            extra: <p>Đơn vị: triệu VND</p>,
          }),
        ]}
      />
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        items={[
          getItems({
            key: '4',
            label: 'Doanh thu hóa đơn',
            children: (
              <TableHOFollowPlanDetail
                year={year}
                loading={getTrackRevenueHoDetailLoading}
                title={'Tiến độ'}
                data={invoiceRevenueProcess}
                ref={invoiceRevenueTableRef}
              />
            ),
            extra: <p>Đơn vị: triệu VND</p>,
          }),
        ]}
      />
    </Flex>
  );
};

export default HOFollowPlanDetail;
